"use client";

import { LineChart, Line } from "@/components/charts/line-chart";
import { Grid } from "@/components/charts/grid";
import { XAxis } from "@/components/charts/x-axis";
import { YAxis } from "@/components/charts/y-axis";
import { ChartTooltip } from "@/components/charts/tooltip";
import { ReferenceArea } from "@/components/charts/reference-area";
import { ReferenceAreaLabel } from "@/components/charts/reference-area-label";
import { resolveCurve, type CurveName } from "@/components/charts/curve-map";
import { ChartBrush, ChartBrushLayout } from "@/components/charts/chart-brush";
import type { ChartBrushLayoutRenderProps } from "@/components/charts/chart-brush";
import { SwipeScroll } from "@/components/charts/swipe-scroll";
import {
  ChartAxisFrame,
  ChartXAxisRow,
} from "@/components/charts/chart-axis-frame";
import { ChartLegendHoverProvider } from "@/components/charts/chart-legend-hover";
import { useLegendClickSync } from "@/components/charts/use-legend-click-sync";
import {
  Legend,
  LegendItem,
  LegendLabel,
  LegendMarker,
} from "@/components/charts/legend";
import { cn } from "@/lib/utils";
import {
  ChartDataPoint,
  ChartThreshold,
  type ModuleChartSeriesMeta,
} from "@/types/modules";
import { z } from "zod";
import type { ModuleReferenceAreaSchema } from "@/types/modules";
import { stripInlineMarkdown } from "@/utils/inline-markdown";
import React, { JSX, useMemo, useState } from "react";
import {
  moduleAxisLabels,
} from "./chart-module-metrics";
import { CHART_PLOT_HEIGHT, MIN_CHART_WIDTH, MIN_PX_PER_POINT } from "@/config/layout";
import { accentChartColors } from "../module-accent";
import { chartPaletteStyle } from "../imports-token-scope";

type ReferenceAreaConfig = z.infer<typeof ModuleReferenceAreaSchema>;

const CHART_HEIGHT = CHART_PLOT_HEIGHT.line;
const PX_PER_POINT = MIN_PX_PER_POINT.line;

export default function ModuleLineChart({
  data,
  xAxisKey = "name",
  xAxisLabel,
  yAxisLabel,
  thresholds,
  series,
  showBrush = false,
  showGrid = true,
  showMarkers = false,
  showLegend,
  fadeEdges = true,
  curve,
  referenceArea,
  grayscale,
  accent,
  className,
}: {
  data?: ChartDataPoint[];
  xAxisKey?: string;
  xAxisLabel?: string;
  yAxisLabel?: string;
  thresholds?: ChartThreshold[];
  series?: ModuleChartSeriesMeta[];
  showBrush?: boolean;
  showGrid?: boolean;
  showMarkers?: boolean;
  showLegend?: boolean;
  fadeEdges?: boolean;
  curve?: CurveName;
  referenceArea?: ReferenceAreaConfig;
  grayscale?: boolean;
  accent?: number;
  className?: string;
}): JSX.Element {
  const palette = accentChartColors(grayscale, accent);
  const dataKeys = React.useMemo(() => {
    if (series && series.length > 0) return series.map((s) => s.key);
    if (!data || data.length === 0) return [];
    const firstItem = data[0];
    if (!firstItem || typeof firstItem !== "object") return [];
    return Object.keys(firstItem).filter((key) => key !== xAxisKey);
  }, [data, xAxisKey, series]);

  const seriesColor = (key: string, index: number) => {
    const meta = series?.find((s) => s.key === key);
    return meta?.color ?? palette[index % palette.length] ?? "#000";
  };
  const seriesLabel = (key: string) => {
    const meta = series?.find((s) => s.key === key);
    return stripInlineMarkdown(meta?.label) || key;
  };

  const chartDataFull = useMemo(() => {
    return (data ?? []).map((d) => ({
      ...d,
      [xAxisKey]: stripInlineMarkdown(String(d[xAxisKey] ?? "")),
    }));
  }, [data, xAxisKey]);

  const yThresholds = useMemo(
    () => (thresholds ?? []).filter((t) => t.axis !== "x"),
    [thresholds],
  );

  const legendVisible = showLegend ?? dataKeys.length > 1;

  const [legendHovered, setLegendHovered] = useState<number | null>(null);
  const {
    activeIndex,
    handleSelect,
    setLabelRef,
    selectedOrigin,
    legendSelectedIndex,
    elementIndex,
    toggleElementIndex,
  } = useLegendClickSync();

  return (
    <ChartLegendHoverProvider
      hoveredIndex={legendHovered}
      onHoverChange={setLegendHovered}
      selectedIndex={activeIndex}
      selectedOrigin={selectedOrigin}
      elementIndex={elementIndex}
      onElementToggle={toggleElementIndex}
    >
    <ChartBrushLayout
      data={chartDataFull as unknown as Record<string, unknown>[]}
      xDataKey={xAxisKey}
    >
      {(_layout: ChartBrushLayoutRenderProps<Record<string, unknown>>) => (
    <div className={cn("flex w-full flex-col gap-3", className)} style={chartPaletteStyle(grayscale, accent)}>
      <ChartAxisFrame {...moduleAxisLabels({ xAxisLabel, yAxisLabel })}>
      <SwipeScroll className="min-w-0 flex-1 pb-1 [mask-image:linear-gradient(to_right,black_0%,black_95%,transparent_100%)] [-webkit-mask-image:linear-gradient(to_right,black_0%,black_95%,transparent_100%)]">
      <div
        style={{
          minWidth: Math.max(MIN_CHART_WIDTH, chartDataFull.length * PX_PER_POINT),
        }}
      >
      <div style={{ height: CHART_HEIGHT }}>
        <LineChart
          data={chartDataFull}
          xDataKey={xAxisKey}
          xScaleKind="band"
          margin={{ top: 10, right: 24, bottom: 8, left: 0 }}
          aspectRatio="auto"
          className="h-full"
        >
          {showGrid && (
            <Grid
              horizontal
              vertical={false}
              highlightRowValues={
                yThresholds.length > 0
                  ? yThresholds.map((t) => t.value)
                  : undefined
              }
              highlightRowStroke={yThresholds[0]?.color ?? (grayscale ? "var(--chart-5)" : "var(--destructive)")}
              highlightRowStrokeDasharray="4,4"
              highlightRowStrokeWidth={1.5}
            />
          )}
          {referenceArea && (
            <>
              <ReferenceArea
                y1={referenceArea.y1}
                y2={referenceArea.y2}
                fill={referenceArea.color ?? "var(--chart-1)"}
                fillOpacity={0.15}
                stroke={referenceArea.color ?? "var(--chart-1)"}
                strokeStyle="dashed"
                strokeWidth={1.5}
                fadeEdges
                fadeEdgesLength={12}
                pattern={
                  referenceArea.variant === "pattern"
                    ? (referenceArea.pattern ?? "diagonal")
                    : "none"
                }
              />
              {referenceArea.label && (
                <ReferenceAreaLabel
                  y2={referenceArea.y2}
                  label={stripInlineMarkdown(referenceArea.label)}
                />
              )}
            </>
          )}
          <YAxis />
          {dataKeys.map((key, index) => (
            <Line
              key={key}
              dataKey={key}
              stroke={seriesColor(key, index)}
              strokeWidth={2}
              showMarkers={showMarkers}
              fadeEdges={fadeEdges}
              curve={resolveCurve(curve)}
            />
          ))}
          <XAxis />
          <ChartTooltip
            showDatePill
            rows={(point) =>
              dataKeys.map((key, i) => ({
                color: seriesColor(key, i),
                label: seriesLabel(key),
                value: Number(point[key] ?? 0),
              }))
            }
          />
        </LineChart>
      </div>
      <ChartXAxisRow />
      </div>
      </SwipeScroll>
      </ChartAxisFrame>

      {showBrush && chartDataFull.length > 0 && (
        <ChartBrush
          data={chartDataFull as unknown as Record<string, unknown>[]}
          xDataKey={xAxisKey}
          variant="time"
          height={36}
          previewSeries={dataKeys.map((key, i) => ({
            dataKey: key,
            color: seriesColor(key, i),
          }))}
        />
      )}

      {legendVisible && (
        <SwipeScroll className="w-full pb-1" fade="auto">
          <Legend
            items={dataKeys.map((key, i) => ({
              label: seriesLabel(key),
              value: data?.length ?? 0,
              color: seriesColor(key, i),
            }))}
            hoveredIndex={legendHovered}
            onHoverChange={setLegendHovered}
            selectedIndex={legendSelectedIndex}
            onSelect={handleSelect}
            onLabelRef={setLabelRef}
            className="flex flex-row gap-6"
          >
            <LegendItem className="flex shrink-0 items-center gap-2">
              <LegendMarker className="h-2.5 w-2.5" />
              <LegendLabel />
            </LegendItem>
          </Legend>
        </SwipeScroll>
      )}
    </div>
      )}
    </ChartBrushLayout>
    </ChartLegendHoverProvider>
  );
}
